class ParallaxImage extends HTMLElement {
  constructor() {
    super();
    this.image = null;
    this.speed = 0.5;
    this.positionX = 50;
    this.positionY = 50;
    this.positionXMobile = null;
    this.positionYMobile = null;
    this.isVisible = false;
    this.observer = null;
    this.rafId = null;
    this.boundHandleScroll = this.handleScroll.bind(this);
    this.boundHandleResize = this.handleResize.bind(this);
  }

  connectedCallback() {
    this.image = this.querySelector('img');
    if (!this.image) return;

    this.speed = parseFloat(this.dataset.speed || '0.5');
    this.positionX = parseFloat(this.dataset.positionX || '50');
    this.positionY = parseFloat(this.dataset.positionY || '50');
    this.positionXMobile = this.dataset.positionXMobile ? parseFloat(this.dataset.positionXMobile) : null;
    this.positionYMobile = this.dataset.positionYMobile ? parseFloat(this.dataset.positionYMobile) : null;
    this.updatePosition();
    this.image.style.transform = 'translate3d(-50%, -50%, 0)';

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          this.isVisible = entry.isIntersecting;
          if (this.isVisible) {
            this.startParallax();
          } else {
            this.stopParallax();
          }
        });
      },
      {
        root: null,
        rootMargin: '100px',
        threshold: 0,
      }
    );

    this.observer.observe(this);

    if (this.isElementVisible()) {
      this.isVisible = true;
      this.startParallax();
    }

    window.addEventListener('resize', this.boundHandleResize, { passive: true });
  }

  disconnectedCallback() {
    this.stopParallax();
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    if (this.observer) {
      this.observer.disconnect();
    }
    window.removeEventListener('resize', this.boundHandleResize);
  }

  isElementVisible() {
    const rect = this.getBoundingClientRect();
    const windowHeight = window.innerHeight || document.documentElement.clientHeight;
    return rect.top < windowHeight + 100 && rect.bottom > -100;
  }

  handleResize() {
    this.updatePosition();
    if (this.isVisible) {
      // Cancel any pending RAF and update immediately on resize
      if (this.rafId !== null) {
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }
      this.updateParallax();
    }
  }

  isMobile() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  updatePosition() {
    if (!this.image) return;
    const isMobile = this.isMobile();
    const x = isMobile && this.positionXMobile !== null ? this.positionXMobile : this.positionX;
    const y = isMobile && this.positionYMobile !== null ? this.positionYMobile : this.positionY;
    this.image.style.left = `${x}%`;
    this.image.style.top = `${y}%`;
  }

  startParallax() {
    this.updateParallax();
    window.addEventListener('scroll', this.boundHandleScroll, { passive: true });
  }

  stopParallax() {
    window.removeEventListener('scroll', this.boundHandleScroll);
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  handleScroll() {
    if (!this.isVisible) return;

    if (this.rafId === null) {
      this.rafId = requestAnimationFrame(() => {
        this.updateParallax();
        this.rafId = null;
      });
    }
  }

  updateParallax() {
    if (!this.image || !this.isVisible) return;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      this.image.style.transform = `translate3d(-50%, -50%, 0)`;
      return;
    }

    const windowHeight = window.innerHeight || document.documentElement.clientHeight;
    const windowCenter = windowHeight / 2;

    const containerRect = this.getBoundingClientRect();
    const containerCenter = containerRect.top + containerRect.height / 2;

    const scrollProgress = (containerCenter - windowCenter) / windowHeight;

    const parallaxOffsetPx = Math.round(scrollProgress * this.speed * 100 * 10) / 10;

    this.image.style.transform = `translate3d(-50%, calc(-50% + ${parallaxOffsetPx}px), 0)`;
  }
}

if (!customElements.get('parallax-image')) {
  customElements.define('parallax-image', ParallaxImage);
}
